/**
 * Protected route component that requires authentication.
 */

import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Require admin role */
  requireAdmin?: boolean;
  /** Auto-redirect to Keycloak instead of showing login prompt */
  autoRedirect?: boolean;
}

/**
 * Wrapper for routes that require authentication.
 *
 * If not authenticated:
 * - With autoRedirect=true: immediately redirects to Keycloak login
 * - With autoRedirect=false (default): shows in-app login prompt
 * If requireAdmin is true, also checks for admin role.
 */
export function ProtectedRoute({
  children,
  requireAdmin = false,
  autoRedirect = false,
}: ProtectedRouteProps) {
  const { isLoading, isAuthenticated, isAdmin, login } = useAuth();
  const location = useLocation();

  // Auto-redirect to Keycloak if not authenticated and autoRedirect is enabled
  useEffect(() => {
    if (!isLoading && !isAuthenticated && autoRedirect) {
      login();
    }
  }, [isLoading, isAuthenticated, autoRedirect, login]);

  // Show loading state while checking auth
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  // Not authenticated
  if (!isAuthenticated) {
    // If autoRedirect is enabled, show loading while redirect happens
    if (autoRedirect) {
      return (
        <div className="flex h-screen items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
        </div>
      );
    }

    // Show login prompt
    return (
      <div className="flex h-screen flex-col items-center justify-center gap-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">Dossier</h1>
          <p className="mt-2 text-gray-600">Corporate Intelligence Platform</p>
        </div>
        <button
          onClick={login}
          className="rounded-lg bg-blue-600 px-6 py-3 text-white font-medium hover:bg-blue-700 transition-colors"
        >
          Sign in with Keycloak
        </button>
      </div>
    );
  }

  // Check admin requirement
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/unauthorized" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

/**
 * Component shown when user doesn't have permission.
 */
export function UnauthorizedPage() {
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4">
      <div className="text-center">
        <h1 className="text-2xl font-bold text-red-600">Access Denied</h1>
        <p className="mt-2 text-gray-600">You don't have permission to access this page.</p>
      </div>
      <a
        href="/"
        className="rounded-lg bg-gray-600 px-6 py-3 text-white font-medium hover:bg-gray-700 transition-colors"
      >
        Go Home
      </a>
    </div>
  );
}
